import React, { useState } from 'react';
import type { RootCauseAnalysis } from '../../types/issue';
import type { GembaNoteRecord, IssueMeasureRecord, StratumRecord } from '../../services/issueMaspService';
import type { IssueEvidenceRecord } from '../../services/issueEvidenceService';
import { paretoSeries } from '../../utils/pareto';
import { IssueEvidenceBoard } from './IssueEvidenceBoard';
import { ParetoChart } from './ParetoChart';

const fieldClass = 'h-8 rounded border border-slate-300 bg-white px-2 text-[12px] dark:border-slate-600 dark:bg-slate-900';
const SUGGESTED_DIMENSIONS = ['Produto', 'Máquina', 'Turno', 'Linha', 'Defeito', 'Período', 'Fornecedor'];

function formatQty(value: number): string {
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 }).format(value);
}

function parseQty(raw: string): number | null {
  const text = raw.trim();
  if (!text) return null;
  const normalized = text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatObserved(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split('-');
  if (!year || !month || !day) return iso;
  return `${day}/${month}/${year}`;
}

function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export const ObservationWorkspace: React.FC<{
  statement: string;
  analysis: RootCauseAnalysis | null;
  editable: boolean;
  saving?: boolean;
  measures: IssueMeasureRecord[];
  strata: StratumRecord[];
  gemba: GembaNoteRecord[];
  evidence: IssueEvidenceRecord[];
  names: Record<string, string>;
  pending?: boolean;
  loadError?: boolean;
  onSaveStatement?: (statement: string) => void;
  onAddStratum?: (dimension: string) => Promise<void>;
  onRemoveStratum?: (id: string) => Promise<void>;
  onAddSlice?: (input: {
    stratumId: string;
    label: string;
    occurrenceCount: number | null;
    amount: number | null;
    measureId: string | null;
  }) => Promise<void>;
  onRemoveSlice?: (id: string) => Promise<void>;
  onAddGemba?: (input: { fact: string; place: string; observedOn: string }) => Promise<void>;
  onRemoveGemba?: (id: string) => Promise<void>;
  onAddEvidence?: (file: File, caption: string) => Promise<void>;
  onDeleteEvidence?: (record: IssueEvidenceRecord) => Promise<void>;
  onOpenCause?: () => void;
  onWorkCause?: () => void;
  focusToken?: string | null;
}> = ({
  statement,
  analysis,
  editable,
  saving,
  measures,
  strata,
  gemba,
  evidence,
  names,
  pending = false,
  loadError = false,
  onSaveStatement,
  onAddStratum,
  onRemoveStratum,
  onAddSlice,
  onRemoveSlice,
  onAddGemba,
  onRemoveGemba,
  onAddEvidence,
  onDeleteEvidence,
  onOpenCause,
  focusToken,
}) => {
  const phenomenon = statement.trim();
  const [draft, setDraft] = useState(statement);
  const [trackedStatement, setTrackedStatement] = useState(statement);
  const [open, setOpen] = useState<'phenomenon' | 'stratum' | 'gemba' | null>(phenomenon ? null : 'phenomenon');
  const [seenFocus, setSeenFocus] = useState(focusToken);
  const [error, setError] = useState('');
  if (focusToken && focusToken !== seenFocus) {
    setSeenFocus(focusToken);
    if (focusToken.includes(':phenomenon')) setOpen('phenomenon');
    if (focusToken.includes(':stratum')) setOpen('stratum');
    if (focusToken.includes(':gemba')) setOpen('gemba');
  }
  if (statement !== trackedStatement) {
    setTrackedStatement(statement);
    setDraft(statement);
  }
  const hasPriorCause = Boolean(analysis?.five_whys?.some((entry) => entry.answer.trim()) || analysis?.ishikawa?.some((group) => group.causes.length > 0));
  const charts = strata.flatMap((stratum) => {
    const series = paretoSeries(stratum.slices, (measureId) => measures.find((measure) => measure.id === measureId)?.unit ?? null);
    return series ? [{ stratum, series }] : [];
  });
  const showStrata = editable || strata.length > 0;
  const showGemba = editable || gemba.length > 0;
  const showEvidence = editable || evidence.length > 0;

  const act = async (work: () => Promise<void>) => {
    setError('');
    try {
      await work();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar.');
    }
  };

  return (
    <div className="min-w-0 space-y-3">
      <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[12px] text-slate-600 dark:text-slate-300">
        <span className="text-[11px] font-semibold tracking-wide text-slate-400">OBSERVAÇÃO</span>
        <span>Fenômeno {phenomenon ? '✓' : '—'}</span>
        <span>Estratificação {strata.length}</span>
        <span>Gemba {gemba.length}</span>
        <span>Evidências {evidence.length}</span>
      </p>

      <section className="rounded-md border border-slate-200 px-3 py-2 dark:border-slate-800">
        <h3 className="text-[12px] font-semibold text-slate-900 dark:text-slate-50">Fenômeno observado</h3>
        {editable && open === 'phenomenon' ? (
          <div className="mt-1">
            <textarea
              aria-label="Fenômeno observado"
              value={draft}
              rows={3}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="O que foi observado, onde e qual o efeito."
              className="w-full resize-y rounded border border-slate-300 px-2 py-1.5 text-[13px] leading-relaxed"
            />
            <button
              type="button"
              disabled={saving}
              onClick={() => onSaveStatement?.(draft)}
              className="mt-1 rounded border border-slate-300 px-2 py-1 text-[12px] font-medium text-slate-700"
            >
              Salvar observação
            </button>
          </div>
        ) : (
          <p className="mt-1 text-[13px] leading-relaxed text-slate-800 dark:text-slate-100">
            {phenomenon || 'O fenômeno ainda não foi descrito.'}
            {editable ? (
              <button type="button" onClick={() => { setDraft(statement); setOpen('phenomenon'); }} className="ml-2 text-[12px] font-medium text-slate-600 hover:underline">Editar</button>
            ) : null}
          </p>
        )}
        {hasPriorCause && onOpenCause ? (
          <button type="button" onClick={onOpenCause} className="mt-1 text-[12px] font-medium text-slate-600 hover:underline">
            As hipóteses estão na análise.
          </button>
        ) : null}
      </section>

      {showStrata || showGemba ? (
        <div className={`grid items-start gap-3 ${showStrata && showGemba ? 'xl:grid-cols-2' : ''}`}>
          {showStrata ? (
            <section className="rounded-md border border-slate-200 px-3 py-2 dark:border-slate-800">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-[11px] font-semibold tracking-wide text-slate-400">Estratificação</h3>
                {editable ? (
                  <button type="button" onClick={() => setOpen('stratum')} className="text-[12px] font-medium text-slate-700 hover:underline dark:text-slate-200">+ Nova estratificação</button>
                ) : null}
              </div>
              {pending && strata.length === 0 ? <p className="mt-1 text-[13px] text-slate-500">Carregando estratificação.</p> : loadError && strata.length === 0 ? <p className="mt-1 text-[13px] text-slate-500">Não foi possível carregar a estratificação.</p> : strata.length === 0 ? <p className="mt-1 text-[13px] text-slate-500">Nenhuma dimensão registrada.</p> : (
                <div className="mt-2 space-y-3">
                  {strata.map((stratum) => (
                    <div key={stratum.id}>
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="text-[13px] font-semibold text-slate-900 dark:text-slate-50">{stratum.dimension}</p>
                        {editable && onRemoveStratum ? (
                          <button type="button" onClick={() => void act(() => onRemoveStratum(stratum.id))} className="text-[12px] text-slate-500 hover:underline">Excluir</button>
                        ) : null}
                      </div>
                      {stratum.slices.length === 0 ? <p className="text-[12px] text-slate-500">Sem fatias.</p> : (
                        <ul className="mt-1 space-y-0.5 text-[12px] text-slate-700 dark:text-slate-200">
                          {stratum.slices.map((slice) => {
                            const measure = measures.find((item) => item.id === slice.measureId);
                            return (
                              <li key={slice.id} className="flex items-baseline justify-between gap-2">
                                <span className="min-w-0 truncate">{slice.label}</span>
                                <span className="shrink-0 tabular-nums text-slate-500">
                                  {slice.occurrenceCount != null ? `${formatQty(slice.occurrenceCount)} occ.` : ''}
                                  {slice.amount != null && measure ? `${slice.occurrenceCount != null ? ' · ' : ''}${formatQty(slice.amount)} ${measure.unit}` : ''}
                                  {editable && onRemoveSlice ? (
                                    <button type="button" onClick={() => void act(() => onRemoveSlice(slice.id))} className="ml-2 text-slate-500 hover:underline">Excluir</button>
                                  ) : null}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {editable && onAddSlice ? (
                        <SliceForm measures={measures} onSubmit={(input) => act(async () => { await onAddSlice({ stratumId: stratum.id, ...input }); })} />
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
              {editable && open === 'stratum' && onAddStratum ? (
                <DimensionForm onSubmit={(dimension) => act(async () => { await onAddStratum(dimension); setOpen(null); })} onClose={() => setOpen(null)} />
              ) : null}
            </section>
          ) : null}

          {showGemba ? (
            <section className="rounded-md border border-slate-200 px-3 py-2 dark:border-slate-800">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-[11px] font-semibold tracking-wide text-slate-400">Gemba</h3>
                {editable ? (
                  <button type="button" onClick={() => setOpen('gemba')} className="text-[12px] font-medium text-slate-700 hover:underline dark:text-slate-200">+ Registrar observação no Gemba</button>
                ) : null}
              </div>
              {pending && gemba.length === 0 ? <p className="mt-1 text-[13px] text-slate-500">Carregando Gemba.</p> : loadError && gemba.length === 0 ? <p className="mt-1 text-[13px] text-slate-500">Não foi possível carregar o Gemba.</p> : gemba.length === 0 ? <p className="mt-1 text-[13px] text-slate-500">Nenhuma observação no Gemba.</p> : (
                <ul className="mt-1 divide-y divide-slate-100 dark:divide-slate-800">
                  {gemba.map((note) => (
                    <li key={note.id} className="py-1.5 text-[12px]">
                      <p className="text-[13px] text-slate-900 dark:text-slate-50">{note.fact}</p>
                      <p className="text-slate-500">
                        {[note.place, formatObserved(note.observedAt), note.observerId ? (names[note.observerId] || 'Observador') : 'Observador não informado'].filter(Boolean).join(' · ')}
                        {editable && onRemoveGemba ? (
                          <button type="button" onClick={() => void act(() => onRemoveGemba(note.id))} className="ml-2 hover:underline">Excluir</button>
                        ) : null}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              {editable && open === 'gemba' && onAddGemba ? (
                <GembaForm onSubmit={(input) => act(async () => { await onAddGemba(input); setOpen(null); })} onClose={() => setOpen(null)} />
              ) : null}
            </section>
          ) : null}
        </div>
      ) : null}

      {charts.map(({ stratum, series }) => (
        <ParetoChart
          key={stratum.id}
          title={stratum.dimension}
          series={series}
          formatValue={(value) => (
            series.basis === 'amount'
              ? `${formatQty(value)} ${measures.find((measure) => measure.id === series.measureId)?.unit ?? ''}`.trim()
              : `${formatQty(value)} occ.`
          )}
        />
      ))}

      {showEvidence ? (
        <IssueEvidenceBoard
          records={evidence}
          editable={editable}
          onAdd={onAddEvidence}
          onDelete={onDeleteEvidence}
          emptyLabel={pending ? 'Carregando evidências.' : loadError ? 'Não foi possível carregar as evidências.' : 'Nenhuma evidência de observação.'}
        />
      ) : null}
      {error ? <p className="text-[12px] text-red-700">{error}</p> : null}
    </div>
  );
};

function DimensionForm({ onSubmit, onClose }: { onSubmit: (dimension: string) => void; onClose: () => void }) {
  const [dimension, setDimension] = useState('');
  const [local, setLocal] = useState('');
  return (
    <form className="mt-2 flex flex-wrap items-center gap-2" onSubmit={(event) => {
      event.preventDefault();
      if (!dimension.trim()) {
        setLocal('Dê um nome à dimensão.');
        return;
      }
      onSubmit(dimension);
    }}>
      <input aria-label="Dimensão" list="stratum-suggestions" value={dimension} onChange={(event) => setDimension(event.target.value)} placeholder="Produto, máquina, turno ou outro" className={`${fieldClass} min-w-0 flex-1`} />
      <datalist id="stratum-suggestions">
        {SUGGESTED_DIMENSIONS.map((item) => <option key={item} value={item} />)}
      </datalist>
      <button type="submit" className="h-8 rounded border border-slate-300 px-2 text-[12px] font-medium">Salvar</button>
      <button type="button" onClick={onClose} className="text-[12px] text-slate-500">Cancelar</button>
      {local ? <p className="basis-full text-[12px] text-red-700">{local}</p> : null}
    </form>
  );
}

function SliceForm({ measures, onSubmit }: {
  measures: IssueMeasureRecord[];
  onSubmit: (input: { label: string; occurrenceCount: number | null; amount: number | null; measureId: string | null }) => void;
}) {
  const [label, setLabel] = useState('');
  const [count, setCount] = useState('');
  const [amount, setAmount] = useState('');
  const [measureId, setMeasureId] = useState(measures[0]?.id ?? '');
  const [local, setLocal] = useState('');
  return (
    <form className="mt-1 flex flex-wrap items-center gap-1.5" onSubmit={(event) => {
      event.preventDefault();
      const occurrenceCount = count.trim() ? parseQty(count) : null;
      const amountValue = amount.trim() ? parseQty(amount) : null;
      if (!label.trim() || (count.trim() && occurrenceCount == null) || (amount.trim() && amountValue == null)) {
        setLocal('Informe a fatia e um número válido.');
        return;
      }
      if (occurrenceCount == null && amountValue == null) {
        setLocal('Informe a contagem ou o valor.');
        return;
      }
      if (amountValue != null && !measureId) {
        setLocal('O valor precisa de uma medida deste problema.');
        return;
      }
      setLocal('');
      onSubmit({
        label,
        occurrenceCount,
        amount: amountValue,
        measureId: amountValue == null ? null : measureId,
      });
      setLabel('');
      setCount('');
      setAmount('');
    }}>
      <input aria-label="Fatia" value={label} onChange={(event) => setLabel(event.target.value)} placeholder="Fatia" className={`${fieldClass} w-28`} />
      <input aria-label="Contagem" value={count} onChange={(event) => setCount(event.target.value)} placeholder="Contagem" className={`${fieldClass} w-24`} />
      <input aria-label="Valor da fatia" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="Valor" className={`${fieldClass} w-20`} />
      {measures.length > 0 ? (
        <select aria-label="Medida da fatia" value={measureId} onChange={(event) => setMeasureId(event.target.value)} className={`${fieldClass} max-w-[10rem]`}>
          {measures.map((measure) => <option key={measure.id} value={measure.id}>{measure.name} ({measure.unit})</option>)}
        </select>
      ) : null}
      <button type="submit" className="h-8 rounded border border-slate-300 px-2 text-[12px]">Adicionar</button>
      {local ? <p className="basis-full text-[12px] text-red-700">{local}</p> : null}
    </form>
  );
}

function GembaForm({ onSubmit, onClose }: {
  onSubmit: (input: { fact: string; place: string; observedOn: string }) => void;
  onClose: () => void;
}) {
  const [fact, setFact] = useState('');
  const [place, setPlace] = useState('');
  const [observedOn, setObservedOn] = useState(todayIso);
  const [local, setLocal] = useState('');
  return (
    <form className="mt-2 grid gap-2" onSubmit={(event) => {
      event.preventDefault();
      if (!fact.trim() || !observedOn) {
        setLocal('Informe o fato e a data.');
        return;
      }
      onSubmit({ fact, place, observedOn });
    }}>
      <textarea aria-label="Fato observado" value={fact} onChange={(event) => setFact(event.target.value)} rows={2} placeholder="Fato observado" className="w-full rounded border border-slate-300 px-2 py-1.5 text-[13px]" />
      <div className="flex flex-wrap gap-2">
        <input aria-label="Local do Gemba" value={place} onChange={(event) => setPlace(event.target.value)} placeholder="Local" className={`${fieldClass} min-w-0 flex-1`} />
        <input aria-label="Data da observação" type="date" value={observedOn} onChange={(event) => setObservedOn(event.target.value)} className={fieldClass} />
        <button type="submit" className="h-8 rounded border border-slate-300 px-2 text-[12px] font-medium">Salvar</button>
        <button type="button" onClick={onClose} className="text-[12px] text-slate-500">Cancelar</button>
      </div>
      {local ? <p className="text-[12px] text-red-700">{local}</p> : null}
    </form>
  );
}
